import { useMemo } from 'react'

import type { PostListItem } from '../types'

type UseSelectedPostDataParams<TChannel extends { id: string }> = {
  posts: PostListItem[]
  selectedPostId?: string
  channels: TChannel[]
}

type SelectedPostData<TChannel> = {
  selectedPost: PostListItem | undefined
  selectedPostChannel: TChannel | undefined
}

/**
 * Derives the currently selected post and its associated channel.
 * When selectedPostId is set (e.g. from URL) but not in the loaded list yet,
 * returns undefined so the UI can show a loading state instead of the first post.
 *
 * Generic over the channel type so the selected channel keeps whatever colocated
 * fragment shape the caller threads through — this hook only reads `id`.
 */
export function useSelectedPostData<TChannel extends { id: string }>({
  posts,
  selectedPostId,
  channels,
}: UseSelectedPostDataParams<TChannel>): SelectedPostData<TChannel> {
  const selectedPost = useMemo(() => {
    if (selectedPostId) {
      return posts.find((post) => post.resolved.id === selectedPostId)
    }
    return posts[0]
  }, [posts, selectedPostId])

  const selectedPostChannel = useMemo(() => {
    if (!selectedPost) return undefined
    return channels.find(
      (channel) => channel.id === selectedPost.resolved.channelId,
    )
  }, [selectedPost, channels])

  return { selectedPost, selectedPostChannel }
}
